import { useRequest } from 'alova/client';
import { $http } from './request';
import { $http as $httpNoBaseUrl } from './requestNoBaseUrl';
import { sleep } from '@/utils';
import { ElMessage } from 'element-plus';
import CryptoJS from 'crypto-js';
import adapterFetch from 'alova/fetch';
import { createAlova } from 'alova';
import VueHook from 'alova/vue';
import { getEnv } from '@/env';

export interface ISrc {
  key: string;
  url: string;
}

const $srcHttp = createAlova({
  id: 'srcHttp',
  baseURL: '',

  cacheFor: {
    // 统一设置POST的缓存模式
    POST: {
      mode: 'memory',
      expire: 1000,
    },
  },
  statesHook: VueHook,
  async beforeRequest(method) {},

  requestAdapter: adapterFetch(),

  responded: {
    onSuccess: async (response, method) => {
      const respText = await response.text();
      return respText;
    },
    onError: (err, method) => {
      return Promise.reject(err);
    },
    onComplete: async (method) => {},
  },
});

/**
 * head请求查询接口延迟
 * @param uploadUrls
 * @returns
 */
const ping = (uploadUrls: string[]): Promise<{ url: string }[]> => {
  return new Promise((resolve, reject) => {
    const queueResult: { url: string }[] = [];
    const countTimes = [];

    // 包装每个请求并记录完成顺序
    uploadUrls.forEach((url, index) => {
      fetch(url+ '/api/single/fileupload', {
        method: 'HEAD',
      })
        .then((resp) => {
          queueResult.push({
            url: url,
          });
        })
        .finally(() => {
          countTimes.push(url);

          // 最后
          if (countTimes.length === uploadUrls.length) {
            // 如果某个请求失败，最后也添加到队列中
            if (queueResult.length < uploadUrls.length) {
              for (const url of uploadUrls) {
                for (const que of queueResult) {
                  if (que.url === url) {
                    continue;
                  }
                  queueResult.push({ url });
                }
              }
            }
            resolve(queueResult);
          }
        });
    });
  });
};

/**
 * 获取可用的上传服务
 * @returns
 */
export const useUploadApi = () => {
  const {
    send: getUploadList,
    loading,
    data: uploadList,
  } = useRequest<any>(
    () => {
      return $http.Post('/api/file/get_upload_list', null, {
        cacheFor: {
          mode: 'memory',
          expire: Infinity,
        },
      });
    },
    {
      immediate: true,
      async middleware(context, next) {
        const data: any = await next();
        console.log(data,'data-----------');
        
        const pingSort = await ping(data.Paths || []);

        return pingSort;
      },
    }
  );

  return {
    uploadList,
    loading,
    getUploadList,
  };
};

/**
 * 上传
 * @returns
 */
export const useUpload = () => {
  const { uploadList } = useUploadApi();
  const uploaded = ref(false);
  const uploading = ref(false);
  const uploadStatus = ref() // 1成功   2失败  3上传中
  const base64Src = ref('');

  const uploadInfo = reactive<
    {
      url: '';
      key: '';
    }[]
  >([]);

  // 成功上传的资源
  const activeSrc = reactive({
    url: '',
    key: '',
  });

  /**
   * 处理上传
   */
  const handleUpload = async (file: any, type?: string) => {
    /**
     * type: 1 不加密  2 加密
     */
    if (!uploadList.value.length) {
      return;
    }
    reset();
    uploading.value = true;
    uploadStatus.value = 3
    for (let i = 0, len = uploadList.value.length; i < len; i++) {
      const item = uploadList.value[i];

      const fd = new FormData();
      fd.append('file', file);
      fd.append('sign_type', type || '2');

      try {
        const resp: any = await $httpNoBaseUrl.Post(item.url + '/api/single/fileupload', fd, {
          cacheFor: 0,
          noTips: true, // 上传失败不进行错误提示
        } as any);

        const keyValue = resp?.paths || [];
        uploadInfo.push(...keyValue);
        await base64DecodeSuccess();
        uploaded.value = true;
        uploading.value = false;
        uploadStatus.value = 1
        return;
      } catch (error) {
        // 所有备选地址都上传失败
        if (i >= uploadList.value.length - 1) {
          uploadStatus.value = 2
          ElMessage.error('上传失败')
        }
      }
    }
    uploaded.value = true;
    uploading.value = false;
  };

  /**
   * 通过key-value解码成base64
   */
  const base64DecodeSuccess = async () => {
    for (let i = 0, len = uploadInfo.length; i < len; i++) {
      const item = uploadInfo[i];
      try {
        // 解码成功，后面就不尝试了
        const resp = await decryptImgFn(item);
        // console.log('解码后...', resp);
        base64Src.value = resp;
        activeSrc.url = item.url;
        activeSrc.key = item.key;
        break;
      } catch (error) {
        console.log('decode error...');
      }
    }
  };

  const reset = () => {
    uploadInfo.length = 0;
    uploaded.value = false;
    uploading.value = false;
    base64Src.value = '';
    activeSrc.key = '';
    activeSrc.url = '';
  };

  return {
    uploaded,
    uploading,
    uploadStatus,
    base64Src,
    uploadInfo,
    activeSrc,
    handleUpload,
  };
};

const suffixList = ['.bmp', '.jpg', '.jpeg', '.png', '.gif', '.pdf'];

/**
 * 图片解密
 * @param url
 * @param key
 * @returns 返回一个base64 或者 url
 */
export const decryptImgFn = async (srcInfo: ISrc = { url: '', key: '' }): Promise<string> => {
  if (!srcInfo.url) {
    return '';
  }
  if (!srcInfo.key) {
    return srcInfo.url;
  }
  let encryptedImageData = '';
  const res = await $srcHttp.Get<string>(srcInfo.url).catch(() => '');
  encryptedImageData = res;

  // 加密图片请求失败
  if (!encryptedImageData) {
    return `${getEnv(import.meta.env.MODE)}/icon-load-fail.svg`;
  }

  let suffix = suffixList.find((s) => srcInfo.url.indexOf(s) > -1);
  if (!suffix) suffix = '.png';
  // key = key.substring(key.indexOf(':') + 1);
  const iv = CryptoJS.enc.Hex.parse('0000000000000000');
  const keyStr = CryptoJS.enc.Utf8.parse(srcInfo.key);
  const decrypted = CryptoJS.AES.decrypt(encryptedImageData, keyStr, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const wordArray = CryptoJS.lib.WordArray.create(decrypted.words);
  const decryptedImageData = CryptoJS.enc.Base64.stringify(wordArray);
  const base64 = `data:image/${suffix.slice(1)};base64,` + decryptedImageData;
  return base64;
};
